import React from 'react';
import { Priority, Status } from '../types';
import { MoreHorizontal } from 'lucide-react';
// Linear-style geometric status icons: a consistent 14px ring with pie-style
// progress fills, so the whole set reads as one crisp family.
export function StatusIcon({
  status,
  className = ''
}: {
  status: Status;
  className?: string;
}) {
  const base = 'shrink-0';
  switch (status) {
    case 'backlog':
      return <svg viewBox="0 0 14 14" fill="none" className={`text-slate-400 ${base} ${className}`} data-id="element-47">
          <circle cx="7" cy="7" r="5.75" stroke="currentColor" strokeWidth="1.5" strokeDasharray="1.4 2.2" strokeLinecap="round" data-id="element-48" />
        </svg>;
    case 'todo':
      return <svg viewBox="0 0 14 14" fill="none" className={`text-slate-400 ${base} ${className}`} data-id="element-49">
          <circle cx="7" cy="7" r="5.75" stroke="currentColor" strokeWidth="1.5" data-id="element-50" />
        </svg>;
    case 'in-progress':
      return <svg viewBox="0 0 14 14" fill="none" className={`text-amber-500 ${base} ${className}`} data-id="element-51">
          <circle cx="7" cy="7" r="5.75" stroke="currentColor" strokeWidth="1.5" data-id="element-52" />
          <path d="M7 7 L7 2.25 A4.75 4.75 0 0 1 7 11.75 Z" fill="currentColor" data-id="element-53" />
        </svg>;
    case 'in-review':
      return <svg viewBox="0 0 14 14" fill="none" className={`text-[#5e6ad2] ${base} ${className}`} data-id="element-54">
          <circle cx="7" cy="7" r="5.75" stroke="currentColor" strokeWidth="1.5" data-id="element-55" />
          <path d="M7 7 L7 2.25 A4.75 4.75 0 0 1 11.14 9.37 Z" fill="currentColor" data-id="element-56" />
        </svg>;
    case 'done':
      return <svg viewBox="0 0 14 14" fill="none" className={`text-[#5e6ad2] ${base} ${className}`} data-id="element-57">
          <circle cx="7" cy="7" r="6" fill="currentColor" data-id="element-58" />
          <path d="M4.4 7.1 L6.15 8.85 L9.7 5.1" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" data-id="element-59" />
        </svg>;
    case 'canceled':
      return <svg viewBox="0 0 14 14" fill="none" className={`text-slate-400 ${base} ${className}`} data-id="element-60">
          <circle cx="7" cy="7" r="6" fill="currentColor" data-id="element-61" />
          <path d="M4.9 4.9 L9.1 9.1 M9.1 4.9 L4.9 9.1" stroke="white" strokeWidth="1.4" strokeLinecap="round" data-id="element-62" />
        </svg>;
    default:
      return <svg viewBox="0 0 14 14" fill="none" className={`text-slate-400 ${base} ${className}`} data-id="element-63">
          <circle cx="7" cy="7" r="5.75" stroke="currentColor" strokeWidth="1.5" data-id="element-64" />
        </svg>;
  }
}
export function PriorityIcon({
  priority,
  className = ''
}: {
  priority: Priority;
  className?: string;
}) {
  switch (priority) {
    case 'no-priority':
      return <MoreHorizontal className={`text-slate-400 ${className}`} data-id="element-65" />;
    case 'urgent':
      return <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className={`text-red-500 ${className}`} data-id="element-66">
          <path d="M7 1.5C7 1.5 7 2.5 7 3.5C7 4.5 7 5.5 7 6.5C7 7.5 7 8.5 7 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-67" />
          <path d="M7 12.5C7.41421 12.5 7.75 12.1642 7.75 11.75C7.75 11.3358 7.41421 11 7 11C6.58579 11 6.25 11.3358 6.25 11.75C6.25 12.1642 6.58579 12.5 7 12.5Z" fill="currentColor" data-id="element-68" />
          <rect x="1.5" y="1.5" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" data-id="element-69" />
        </svg>;
    case 'high':
      return <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className={`text-slate-600 ${className}`} data-id="element-70">
          <path d="M2.5 11.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-71" />
          <path d="M7 11.5V5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-72" />
          <path d="M11.5 11.5V2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-73" />
        </svg>;
    case 'medium':
      return <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className={`text-slate-500 ${className}`} data-id="element-74">
          <path d="M2.5 11.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-75" />
          <path d="M7 11.5V5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-76" />
          <path d="M11.5 11.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-77" />
        </svg>;
    case 'low':
      return <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className={`text-slate-400 ${className}`} data-id="element-78">
          <path d="M2.5 11.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-79" />
          <path d="M7 11.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-80" />
          <path d="M11.5 11.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-81" />
        </svg>;
    default:
      return <MoreHorizontal className={`text-slate-400 ${className}`} data-id="element-82" />;
  }
}